import { useTranslation } from 'react-i18next';
import { telegram } from '../../../utils/telegram';
import { RANGE_VALUES } from './constants';
import type { RangeKey } from '../../../types/provider';

interface Props {
  value: RangeKey;
  onChange: (value: RangeKey) => void;
}

export default function TimeRangeSwitcher({ value, onChange }: Props) {
  const { t } = useTranslation('provider');

  const handleChange = (newValue: RangeKey) => {
    if (newValue === value) return;
    telegram.hapticFeedback.selection();
    onChange(newValue);
  };

  const activeIndex = Math.max(0, RANGE_VALUES.indexOf(value));
  const segmentCount = RANGE_VALUES.length;

  return (
    <div
      role="tablist"
      style={{
        position: 'relative',
        display: 'flex',
        background: 'var(--bl-surface-soft, #f0f2f5)',
        borderRadius: 12,
        padding: 3,
        height: 36,
      }}
    >
      {/* Sliding indicator */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 3,
          bottom: 3,
          left: 3,
          width: `calc((100% - 6px) / ${segmentCount})`,
          transform: `translateX(${activeIndex * 100}%)`,
          background: 'var(--bl-surface, #fff)',
          borderRadius: 9,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {RANGE_VALUES.map((key) => {
        const isActive = value === key;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={isActive}
            onClick={() => handleChange(key)}
            style={{
              position: 'relative',
              flex: 1,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              color: isActive ? 'var(--bl-text-strong, #0f172a)' : 'var(--bl-text-muted, #6b7280)',
              transition: 'color 0.2s ease, font-weight 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
              padding: 0,
              letterSpacing: isActive ? -0.1 : 0,
            }}
          >
            {t(`bookings.rangeOptions.${key}`)}
          </button>
        );
      })}
    </div>
  );
}
